import { useState, useRef } from 'react';
import type { FC } from 'react';
import { capitalCase } from 'change-case';
import {
  Badge,
  Box,
  Button,
  IconButton,
  Popover,
  SvgIcon,
  Typography,
  Select,
  MenuItem,
} from '@mui/material';
import { Sun as SunIcon } from 'react-feather';
import useSettings from 'src/hooks/useSettings';
import { THEMES } from 'src/constants';
import HtmlTooltip from 'src/components/HtmlTooltip';

const Settings: FC = () => {
  const ref = useRef<HTMLButtonElement>(null);
  const { settings, saveSettings } = useSettings();
  const [isOpen, setOpen] = useState<boolean>(false);
  const [values, setValues] = useState({
    direction: settings.direction,
    responsiveFontSizes: settings.responsiveFontSizes,
    theme: settings.theme,
  });

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleChange = (field, value): void => {
    setValues({
      ...values,
      [field]: value,
    });
  };

  const handleSave = (): void => {
    saveSettings(values);
    setOpen(false);
  };

  return (
    <Box>
      <HtmlTooltip title="Theme">
        <Badge
          color="secondary"
          sx={{
            '& .MuiBadge-badge': {
              height: 8,
              width: 8,
              borderRadius: 4,
              marginTop: 1,
              marginRight: 0.5,
            },
          }}
        >
          <IconButton
            color="inherit"
            onClick={handleOpen}
            ref={ref}
            size="small"
            sx={{ marginRight: 0.5 }}
          >
            <SvgIcon fontSize="small">
              <SunIcon />
            </SvgIcon>
          </IconButton>
        </Badge>
      </HtmlTooltip>
      <Popover
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 220,
            p: 1.5,
            borderRadius: 1,
          },
        }}
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        elevation={3}
      >
        <Box sx={{ p: 1 }}>
          <Typography variant="h6" color="textPrimary">
            Theme
          </Typography>
        </Box>
        {/* Box components for RTL and Responsive font sizes are commented out */}
        <Box sx={{ px: 1, pb: 1.5 }}>
          <Select
            fullWidth
            label="Theme"
            name="theme"
            onChange={(event) => handleChange('theme', event.target.value)}
            value={values.theme}
            variant="outlined"
            size="small"
          >
            {Object.keys(THEMES).map((theme) => (
              <MenuItem key={theme} value={theme}>
                {capitalCase(theme)}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Box sx={{ px: 1, pb: 1.5 }}>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={handleSave}
            fullWidth
          >
            Save Settings
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};

export default Settings;
